import React, { useEffect, useRef, useState } from 'react'
import { useLayout } from '../../../_metronic/layout/core'
import { getDetailExperience } from '../../services/teamExperienceService'
import { ExperienceShort } from '../teamExperiences/models/ExperienceSummaryModel';
import Pagination from '../components/Pagination';
import { useHistory, useLocation } from 'react-router-dom';
import { DetailExperienceDrawer } from './components/DetailExperienceDrawer'
import { EXPERIENCE_STATUS, EXPERIENCE_STATUS_LIST } from '../teamExperiences/models/ExperienceStatus';
import { getExperiencesByStatus } from '../../services/adminExperiencesService';
import { KTSVG } from '../../../_metronic/helpers';
import { LoaderOverlay } from '../../hooks/loader/LoaderOverlay';
import { getSuggestions } from '../../services/suggestionService';
import { SuggestedExperienceTable } from './components/SuggestedExperienceTable';
import { DetailSuggestionDrawer } from './components/DetailSuggestionDrawer'
import { PreLaunchLeaderDrawer } from './components/PreLaunchLeaderDrawer'

interface SearchProps {
    limit:number;
    offset:number;
    status:string;
    search?:string;
    type?:string;
}

// The admin experience tabs. `id` is the selection key (also the status filter sent to the API),
// `code` drives the badge counts, `name` is the visible label. Hoisted to module scope so the
// ?tab= deep-link helper can resolve a tab before the component's state initialises.
const experienceStatusList:any = [
  { id: EXPERIENCE_STATUS.PUBLISHED, code: 'active', name: 'Active' },
  { id: `${EXPERIENCE_STATUS.PENDING},${EXPERIENCE_STATUS.DRAFT},${EXPERIENCE_STATUS.PUBLISHED},${EXPERIENCE_STATUS.PAST}`, code: 'pending', name: 'Members' },
  { id: `${EXPERIENCE_STATUS.DRAFT},${EXPERIENCE_STATUS.PENDING},${EXPERIENCE_STATUS.PUBLISHED},${EXPERIENCE_STATUS.PRE_LAUNCH},${EXPERIENCE_STATUS.PAST}`, code: 'pending', name: 'Partners' },
  { id: 'suggested', code: 'suggested', name: 'Suggested' },
  { id: EXPERIENCE_STATUS.PRE_LAUNCH, code: 'preLaunch', name: 'Pre launch' },
  { id: EXPERIENCE_STATUS.REJECTED, code: 'declined', name: 'Decline' },
]

// Resolve a ?tab=<name> query value to a tab id. Matching is on the visible `name`, case-insensitive.
const tabIdFromSearch = (search: string): any => {
  const name = new URLSearchParams(search).get('tab')
  if (!name) return undefined
  const match = experienceStatusList.find((t: any) => t.name.toLowerCase() === name.toLowerCase())
  return match ? match.id : undefined
}

const ExperienceAdminPage: React.FC = () => {
    const { config, setLayout } = useLayout()
    const restoreRef = useRef(config)
    const location = useLocation()

    const [isLoader, setIsLoader] = useState(true);
    const [messageLoader, setMessageLoader] = useState("Loading...")

    const [tabs, setTabs] = useState<any>()
    const [activeTab, setActiveTab] = useState<any>(() => tabIdFromSearch(location.search) ?? experienceStatusList[0].id)
    const [searchTerm, setSearchTerm] = useState('')
    const [experiences, setExperiences] = useState<ExperienceShort[]>([]);
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [selectedSuggestion, setSelectedSuggestion] = useState<any | null>(null);
    const [openDetail, setOpenDetail] = useState(false)
    const [experience, setExperience] = useState(0)
    const [openPreLaunchModal, setOpenPreLaunchModal] = useState(false)
    const [preLaunchExperience, setPreLaunchExperience] = useState<any>(null)
    const [totalRows, setTotalRows] = useState<any>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const history = useHistory()

    const limit = 10;

    const fetchData = async (params: SearchProps, showLoader: boolean = false) => {
        try {
            setIsLoader(true);
            if (params.status === 'suggested') {
                if (params.search === '') delete params.search;
                const data = await getSuggestions({
                    limit: params.limit,
                    offset: params.offset,
                    search: params.search,
                });
                setSuggestions(data?.results ?? []);
                setIsLoader(false);
                setTotalRows(Number(data?.count ?? 0));
                return;
            }

            const currentTab = experienceStatusList.find((t: any) => String(t.id) === String(params.status));
            const tabName = currentTab?.name?.toLowerCase();

            if (tabName === 'members') {
                params.type = 'member';
            } else if (tabName === 'partners') {
                params.type = 'partner';
            } else {
                delete params.type;
            }

            if (params.search == '') delete params.search;
            const experiences_ = await getExperiencesByStatus(params)
            setExperiences(experiences_.results);
            setTabs(experiences_.stats);
            setIsLoader(false);
            setTotalRows(Number(experiences_?.count));
        } catch (error) {
            history.goBack();
        }
    }
    
    const getExperienceDetail = async (id:number) => {
        const experience_ = await getDetailExperience(id);
        setExperience(experience_);
    }

    useEffect(() => {
        restoreRef.current = config
        setLayout({
            ...config,
            header: { ...config.header, display: true },
            aside: { ...config.aside, display: true },
            toolbar: { ...config.toolbar, display: false },
            footer: { ...config.footer, display: true },
        })
        return () => {
            setLayout(restoreRef.current)
        }
    }, [])

    useEffect(() => {
        const tabId = tabIdFromSearch(location.search);
        if (tabId !== undefined && tabId !== activeTab) {
            setActiveTab(tabId);
            setCurrentPage(1);
            fetchData({ limit, offset: 0, status: tabId, search: searchTerm });
        }
    }, [location.search]);

    useEffect(() => {
        setCurrentPage(1);
        const handler = setTimeout(() => {
            fetchData({ limit, offset: 0, status: (activeTab ?? experienceStatusList[0].id), search: searchTerm });
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const totalPages = totalRows ? Math.ceil(totalRows / limit) : 1;

    const changeTab = async (status: string) => {
        setActiveTab(status);
        setCurrentPage(1);
        const tab = experienceStatusList.find((t: any) => String(t.id) === String(status));
        history.replace(tab ? `${location.pathname}?tab=${encodeURIComponent(tab.name)}` : location.pathname);
        await fetchData({ limit, offset: 0, status, search: searchTerm });
    }

    const handlePageChange = async (page: number) => {
        const offset = (page - 1) * limit;
        await fetchData({ limit, offset, status: activeTab });
        setCurrentPage(page);
    };

    const searchInList = (event: any) => {
        if (event.key === 'Enter') {
            setCurrentPage(1);
            fetchData({ limit, offset: 0, status: activeTab, search: searchTerm });
        }
    }

    const handleOpenDetail = async (item: any) => {
        if (
            activeTab === EXPERIENCE_STATUS.PRE_LAUNCH
        ) {
            setPreLaunchExperience(item);
            setOpenPreLaunchModal(true);
            return;
        }
        await getExperienceDetail(item.id);
        await setOpenDetail(true);
    }

    const renderCells = (
        items: any[],
        isLoading: boolean,
        getValue: (u: any) => React.ReactNode,
        keyPrefix: string,
        skeletonWidth: string = '70%',
        styleCell: 'admin-experience-page-table__cell--bold' | '' = 'admin-experience-page-table__cell--bold',
        isDate: boolean = false,
    ) => {
        if (isLoading) {
            return Array.from({ length: limit }).map((_, index) => (
                <div key={`${keyPrefix}-${index}`}
                    className={`table__cell ${index === 0 ? 'table__cell--bold' : ''}`}>
                    <span className='skeleton skeleton--text' style={{ width: skeletonWidth }} />
                </div>
            ))
        }
        return items.map((u, index) => (
            <div onClick={() => handleOpenDetail(u)}
                key={`${keyPrefix}-${index}`}
                className={`cursor-pointer admin-experience-page-table__cell ${styleCell}`}>
                {keyPrefix === 'submittedBy'
                    ? getValue({ name: u.submittedBy?.name || u.name, image: u.submittedBy?.image || u.image })
                    : getValue(u)}
            </div>
        ))
    }

    return (<div className='admin-experience-page'>
        <div className='admin-experience-page__container'>
            <div className='w-100 d-flex flex-row justify-content-between align-items-center mb-4'>
                <h1 className='admin-experience-page__container__title mb-0'>Experiences</h1>
            </div>
            <div className="card">
                <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-bottom-0">
                    {experienceStatusList.map((item: any, index: number) => (<div className='d-flex flex-row' key={`tabs-experiences-${index}`}>
                        <li className="nav-item">
                            <a className={`nav-link text-capitalize ${activeTab === item.id ? "active" : ""}`}
                                onClick={() => changeTab(item.id)}>
                                {item.name}
                                {tabs && (<div className={`badge-wrapper ${tabs && tabs[item.code] && Number(tabs[item.code]) > 0 ? 'd-flex' : 'd-none'}`}>
                                    <div className='badge-count'>{tabs[item.code]}</div>
                                </div>)}
                            </a>
                        </li>
                        <li className={`separator-vertical ${index < experienceStatusList.length - 1 ? 'd-flex' : 'd-none'}`}>
                            <hr />
                        </li>
                    </div>))}
                </ul>
            </div>

            <div className='admin-experience-page__content'>
                {/* Search Bar & Actions */}
                <div className='d-flex flex-row justify-content-between align-items-center mb-4 gap-3'>
                    <div className='search-bar flex-grow-1'>
                        <div className='search-bar__icon'>
                            <div className='search-icon__circle'></div>
                            <div className='search-icon__handle'></div>
                        </div>
                        <input
                            type='text'
                            className='search-bar__input'
                            placeholder='Search Experience'
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => { searchInList(e) }}
                        />
                    </div>
                    {activeTab === 'suggested' ? (
                        <div
                            role='button'
                            className="tap-add-experience-2-btn-main tap-flex-center cursor-pointer text-nowrap"
                            onClick={() => history.push('/experiences/suggest')}
                        >
                            <div className='tap-host-button-text tap-flex-center'>
                                suggest experience &nbsp;<KTSVG path='/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg' />
                            </div>
                        </div>
                    ) : (
                        <div
                            role='button'
                            className="tap-add-experience-2-btn-main tap-flex-center cursor-pointer text-nowrap"
                            onClick={() => history.push('/my-experience')}
                        >
                            <div className='tap-host-button-text tap-flex-center'>
                                Create Experience &nbsp;<KTSVG path='/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg' />
                            </div>
                        </div>
                    )}
                </div>

                {/* Table */}
                {activeTab === 'suggested' ? (
                    <SuggestedExperienceTable
                        items={suggestions}
                        isLoading={isLoader}
                        limit={limit}
                        onSelectSuggestion={(sug) => setSelectedSuggestion(sug)}
                    />
                ) : (
                    <div className={`admin-experience-page-table ${experiences.length > 0 ? 'd-flex' : 'd-none'}`}>
                        {/* ID Column */}
                        <div className='admin-experience-page-table__column admin-experience-page-table__column--80'>
                            <div className='admin-experience-page-table__header'>ID</div>
                            {renderCells(experiences, isLoader, (u) => u.id, 'id')}
                        </div>

                        {/* Name Column */}
                        <div className='admin-experience-page-table__column admin-experience-page-table__column--200'>
                            <div className='admin-experience-page-table__header'>NAME</div>
                            {renderCells(experiences, isLoader, (u) => u.title, 'title')}
                        </div>

                        {/* Source Column */}
                        <div className='admin-experience-page-table__column admin-experience-page-table__column--150'>
                            <div className='admin-experience-page-table__header'>SUBMITTED BY</div>
                            {renderCells(
                                experiences,
                                isLoader,
                                (u) => (<div className="experience-preview-invited-item">
                                    <img src={u.image} />
                                    <div className='text-dark admin-experience-page-table__cell--bold'>{u.name}</div>
                                </div>),
                                'submittedBy',
                                '80%')}
                        </div>

                        {/* Country Column */}
                        <div className='admin-experience-page-table__column admin-experience-page-table__column--80'>
                            <div className='admin-experience-page-table__header'>TYPE</div>
                            {renderCells(experiences, isLoader, (u) => u.typeDisplay, 'typeDisplay')}
                        </div>

                        {/* Requested On Column */}
                        <div className='admin-experience-page-table__column admin-experience-page-table__column--150'>
                            <div className='admin-experience-page-table__header'>DATE UPLOADED</div>
                            {renderCells(experiences, isLoader, (u) => u.dateUploaded, 'dateUploaded')}
                        </div>

                        {/* Category Column */}
                        <div className='admin-experience-page-table__column admin-experience-page-table__column--150'>
                            <div className='admin-experience-page-table__header'>DAYS LEFT</div>
                            {renderCells(experiences, isLoader, (u) => u.daysLeft, 'daysLeft')}
                        </div>

                        {/* Assigned Column */}
                        <div className='admin-experience-page-table__column admin-experience-page-table__column--80'>
                            <div className='admin-experience-page-table__header'>STATUS</div>
                            {renderCells(experiences, isLoader, (u) => {
                                let status = EXPERIENCE_STATUS_LIST.find((item) => item.id == u.status);
                                return status.name;
                            }, 'status')}
                        </div>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalRows > 0 && (<div className='tap-flex-center mt-5'>
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>)}
            {/* <LoaderOverlay visible={isLoader} message={messageLoader} /> */}
        </div>
        <DetailExperienceDrawer 
            experience={experience} 
            open={openDetail} 
            onClose={setOpenDetail} 
            isPartner={activeTab === `${EXPERIENCE_STATUS.DRAFT},${EXPERIENCE_STATUS.PENDING}`}
        />
        <DetailSuggestionDrawer
            open={Boolean(selectedSuggestion)}
            onClose={() => setSelectedSuggestion(null)}
            suggestionId={selectedSuggestion?.id}
        />
        <PreLaunchLeaderDrawer
            open={openPreLaunchModal}
            onClose={() => setOpenPreLaunchModal(false)}
            experience={preLaunchExperience}
            onSuccess={() => fetchData({ limit, offset: (currentPage - 1) * limit, status: activeTab, search: searchTerm })}
        />
    </div>)
}

export default ExperienceAdminPage
